import { z } from 'zod';

export const Vec3 = z.tuple([z.number(), z.number(), z.number()]);
export type Vec3 = z.infer<typeof Vec3>;

export const Waypoint = z.object({
  id: z.string().min(1),
  at: z.number().min(0).max(1),
  position: Vec3,
  lookAt: Vec3,
  ease: z.string().optional(),
});
export type Waypoint = z.infer<typeof Waypoint>;

export const Discipline = z.enum([
  'arch',
  'structure',
  'mep',
  'interior',
  'space',
  'site',
  'other',
]);
export type Discipline = z.infer<typeof Discipline>;

export const ModelAsset = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/, 'kebab-case id'),
  glb: z.string().endsWith('.glb'),
  props: z.string().endsWith('.json').optional(),
  discipline: Discipline.default('arch'),
  label: z.string().optional(),
  tint: z.string().optional(),
  credit: z
    .object({
      title: z.string(),
      author: z.string(),
      licence: z.string(),
      url: z.string().url().optional(),
    })
    .optional(),
});
export type ModelAsset = z.infer<typeof ModelAsset>;

export const Skill = z.enum(['fullstack', 'csharp-fsharp', 'bim', 'ai', 'leadership']);
export type Skill = z.infer<typeof Skill>;

export const StationKind = z.enum([
  'intro',
  'revit',
  'build',
  'compare',
  'drawings',
  'rules',
  'world',
  'projects',
]);
export type StationKind = z.infer<typeof StationKind>;

export const Project = z.object({
  name: z.string().min(1),
  client: z.string().optional(),
  years: z.string().min(1),
  stack: z.array(z.string()).default([]),
});
export type Project = z.infer<typeof Project>;

export const ProjectEntry = z
  .object({
    name: z.string().min(1),
    years: z.string().min(1).optional(),
    summary: z.string().min(1),
    stack: z.array(z.string()).default([]),
    url: z.string().url().or(z.literal('')).default(''),
    client: z.string().optional(),
    clientVisible: z.boolean().default(false),
  })
  .refine((p) => p.clientVisible || !p.client, 'client named while clientVisible is false');
export type ProjectEntry = z.infer<typeof ProjectEntry>;

export const Station = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/, 'kebab-case id'),
  index: z.number().int().nonnegative(),
  title: z.string().min(1),
  // short label for the header anchors, the title when missing
  nav: z.string().optional(),
  subtitle: z.string().optional(),
  body: z.string(),
  skills: z.array(Skill).default([]),
  kind: StationKind.default('intro'),
  project: Project.optional(),
  position: Vec3.default([0, 0, 0]),
  rotationY: z.number().default(0),
  scale: z.number().positive().default(1),
  layers: z.array(ModelAsset).default([]),
  fx: z
    .object({ build: z.boolean().default(false), xray: z.boolean().default(false) })
    .default({ build: false, xray: false }),
  placeholder: z.boolean().default(false),
  waypoints: z.array(Waypoint).min(1),
});
export type Station = z.infer<typeof Station>;

export const Hero = z.object({
  position: Vec3.default([0, 0, 0]),
  rotationY: z.number().default(0),
  layers: z.array(ModelAsset).min(1),
  propsLayer: z.string().min(1),
  raw: z.string().endsWith('.json').optional(),
  // Editable source for the store worker; loaded on demand, never on the landing.
  ifc: z.string().endsWith('.ifc').optional(),
  cameraScale: z.number().positive().default(1),
  name: z.string().min(1),
});
export type Hero = z.infer<typeof Hero>;

export const Tour = z.object({
  title: z.string(),
  hero: Hero,
  heroes: z.record(z.string(), Hero).default({}),
  stations: z.array(Station).min(1),
});
export type Tour = z.infer<typeof Tour>;
export type TourInput = z.input<typeof Tour>;

export function validateTour(input: unknown): Tour {
  const tour = Tour.parse(input);
  const ids = new Set<string>();
  const sorted = [...tour.stations].sort((a, b) => a.index - b.index);
  sorted.forEach((s, i) => {
    if (ids.has(s.id)) throw new Error(`Duplicate station id "${s.id}"`);
    ids.add(s.id);
    if (s.index !== i)
      throw new Error(`Station indices must be contiguous; got ${s.index} at ${i}`);
  });
  for (const h of [tour.hero, ...Object.values(tour.heroes)])
    if (!h.layers.some((l) => l.id === h.propsLayer))
      throw new Error(`hero "${h.name}": propsLayer "${h.propsLayer}" is not one of its layers`);
  const ats = sorted.flatMap((s) => s.waypoints.map((w) => w.at));
  for (let i = 1; i < ats.length; i++) {
    const prev = ats[i - 1]!;
    const cur = ats[i]!;
    if (cur < prev) {
      throw new Error(
        `Waypoint progress must be non-decreasing across the tour (${prev} → ${cur})`,
      );
    }
  }
  return { ...tour, stations: sorted };
}
